import { localNow } from "@/shared/local-now";
import { DataTable } from "@/components/data-table";
import { Form, SchemaFormHook, useSchemaForm } from "@/components/form";
import { Dialog, ModalHeader, ModalValueExtractor, useOpenModal } from "@/components/modal";
import { useModalHandlerContext } from "@/components/modal/contexts/modal-handler-context";
import { usePrinter } from "@/components/print";
import { getCliente } from "@/features/auxiliar/clientes/api/get-cliente";
import { getVendedor } from "@/features/auxiliar/vendedores/api/get-vendedor";
import { useCreateAndPayVentaMutation } from "@/features/cajas/caja-actual/api/create-and-pay-venta";
import { useCreateAndPayVentaConFEMutation } from "@/features/cajas/caja-actual/api/create-and-pay-venta-fe";
import CajaPagoForm, { cajaPagoFormSchema } from "@/features/cajas/caja-actual/components/caja-pago-form";
import { useCajaContext } from "@/features/cajas/caja-actual/contexts/caja-context";
import { useConfigurationContext } from "@/features/configuration/contexts/configuration-context";
import { useOperationalMode } from "@/features/configuration/hooks/use-operational-mode";
import { useAddVentaMutation } from "@/features/inventario/ventas/api/add-venta";
import { useCreateVentaConFECreditoMutation } from "@/features/inventario/ventas/api/create-venta-con-fe-credito";
import { VentaForm, ventaFormSchema } from "@/features/inventario/ventas/components/venta-form";
import { ventaConFEFormSchema } from "@/features/inventario/ventas/schemas/venta-con-fe-form-schema";
import VentaPrintForm from "@/features/inventario/ventas/components/venta-print-form";
import useMonetizacion from "@/features/monetizacion/hooks/use-monetizacion";
import { notify, SilentSubmitError } from "@/shared/notification";
import { EmisorConfigRequiredDialog, useCheckEmisorConfig } from "@/features/facturacion-electronica/components/config-required-alert";
import { Container, Stack, Typography, Checkbox, FormControlLabel, Button, Box } from "@mui/material";
import { z } from "zod/v4";
import { useState } from "react";
import { useVentaDraft } from "@/features/inventario/ventas/hooks/use-venta-draft";
import VentaDraftRestore from "@/features/inventario/ventas/components/venta-draft-restore";
import { useEmisorConfig } from "@/features/facturacion-electronica/hooks/use-emisor-config";
import { buildCreateAndPayFERequest } from "@/features/cajas/caja-actual/utils/build-create-and-pay-fe-request";
import { buildCreateVentaFERequest } from "@/features/cajas/caja-actual/utils/build-create-venta-fe-request";
import { openPdfFromBase64 } from "@/features/facturacion-electronica/utils/pdf-utils";
import { MonedaOperacionField } from "@/features/cajas/caja-actual/components/moneda-operacion-field";
import { OperationalMode } from "@/shared/types/operational-mode";

const getVentaDefaultValues = (facturacionEnabled: boolean, genericClientId: string) =>
    facturacionEnabled ? {
        clienteId: genericClientId,
        iTipTra: 1,
        iTipEmi: 1,
        iIndPres: 1,
        dCondTiCam: 1
    } as any : {
        clienteId: genericClientId
    }

const handleCreateVentaConFECredito = async (
    value: any,
    monedaBase: any,
    createVentaConFECreditoMutation: any,
    dEst?: string,
    dPunExp?: string,
    monedaOperacionId?: string,
    dTiCam?: number
) => {

    const requestData = buildCreateVentaFERequest({
        ventaFormValue: value,
        monedaBase,
        dEst: dEst || "",
        dPunExp: dPunExp || "",
        monedaOperacionId,
        dTiCam
    });

    const result = await createVentaConFECreditoMutation.mutateAsync(requestData as any);

    if (result.facturaElectronica?.kudeBase64) {
        openPdfFromBase64(result.facturaElectronica.kudeBase64);
    }

    notify(`La venta se ha creado correctamente. Se creo una factura electrónica N° ${result.facturaElectronica?.numeroDE}.`);

    return result;
}

export default function NuevaVentaPage() {
    const { paySeparately } = useConfigurationContext();

    return <Container flex={1} component={Stack} maxWidth="xl" gap={3} paddingY={3}>
        <Stack direction={"row"} alignItems={"center"}>
            <Typography component={"h1"} variant="h5">Nueva Venta</Typography>
        </Stack>

        {paySeparately ? <AddVentaForm /> : <AddAndPayVentaForm />}
    </Container>
}

// Formulario para crear venta sin pago
function AddVentaForm() {
    const printer = usePrinter();
    const { genericClientId } = useConfigurationContext();
    const { facturacionEnabled, mode } = useOperationalMode();
    const { monedaBase } = useMonetizacion();
    const createVentaConFECreditoMutation = useCreateVentaConFECreditoMutation();
    const { config } = useEmisorConfig();
    const openModal = useOpenModal();
    const ventaMutation = useAddVentaMutation();
    const schema = facturacionEnabled ? ventaConFEFormSchema : ventaFormSchema;

    const form = useSchemaForm({
        schema: schema,
        onSubmit: async ({ value, formApi }) => {
            const isCredito = value.credito === true;
            const isPresupuesto = value.presupuesto === true;

            const resetKeepVendedor = () => {
                const vendedorId = formApi.getFieldValue("vendedorId");
                formApi.reset();
                formApi.setFieldValue("vendedorId", vendedorId);
                deleteCurrentDraft();
            };

            let venta;
            if (facturacionEnabled && isCredito && !isPresupuesto) {
                openModal("confirmFECredito", {
                    ventaRequest: value,
                    onSuccess: resetKeepVendedor
                });
                return;
            } else {
                venta = await (ventaMutation.mutateAsync as any)(value);
                openModal("printVenta", venta);
                notify("Se ha creado la venta correctamente.");
                resetKeepVendedor();
            }
        },
        defaultValues: getVentaDefaultValues(facturacionEnabled, genericClientId)
    })

    const { otherDrafts, deleteCurrentDraft, deleteDraft } = useVentaDraft(form);

    const handleRestore = (draft: any) => {
        const v = draft.values;
        form.setFieldValue("vendedorId", v.vendedorId ?? "");
        form.setFieldValue("clienteId", v.clienteId ?? "");
        form.setFieldValue("fecha", v.fecha ?? localNow());
        form.setFieldValue("credito", v.credito ?? false);
        form.setFieldValue("presupuesto", v.presupuesto ?? false);
        form.setFieldValue("items", v.items ?? []);
        deleteDraft(draft.id);
    };

    return <>
        <VentaForm form={form as any} flex={1} onAfterReset={deleteCurrentDraft}
            draftRestoreSlot={<VentaDraftRestore drafts={otherDrafts} onRestore={handleRestore} onDelete={deleteDraft} />}
        />
        <printer.Printer />
        <Dialog code="printVenta" size="xs">
            <ModalValueExtractor>
                {venta => <>
                    <ModalHeader>Imprimir Venta N°{venta.numeroVenta}</ModalHeader>
                    <VentaPrintForm venta={venta} padding={3} />
                </>}
            </ModalValueExtractor>
        </Dialog>
        <Dialog code="confirmFECredito" size="xs">
            <ModalValueExtractor>
                {(data) => <ConfirmFECreditoDialog
                    ventaRequest={data.ventaRequest}
                    monedaBase={monedaBase}
                    createVentaConFECreditoMutation={createVentaConFECreditoMutation}
                    createMutation={ventaMutation}
                    config={config}
                    mode={mode}
                    onSuccess={data.onSuccess}
                />}
            </ModalValueExtractor>
        </Dialog>
    </>
}

const createAndPayVentaFormSchema = z.object({
    ventaRequest: ventaFormSchema,
    pagoRequest: cajaPagoFormSchema
})

const createAndPayVentaConFEFormSchema = z.object({
    ventaRequest: ventaConFEFormSchema,
    pagoRequest: cajaPagoFormSchema
})

// Formulario para crear y pagar venta
function AddAndPayVentaForm() {
    const openModal = useOpenModal();
    const { isCajaOpen, hasCajaAssigned } = useCajaContext();
    const { facturacionEnabled, mode } = useOperationalMode();
    const { monedaBase, transferenciaMetodoBase, cotizacion, transferenciaMetodos } = useMonetizacion();
    const { genericClientId } = useConfigurationContext();
    const { config } = useEmisorConfig();
    const checkEmisorConfig = useCheckEmisorConfig();

    const printer = usePrinter();
    const createAndPayMutation = useCreateAndPayVentaMutation();
    const createAndPayFEMutation = useCreateAndPayVentaConFEMutation();
    const createVentaConFECreditoMutation = useCreateVentaConFECreditoMutation();
    const createMutation = useAddVentaMutation();

    const formSchema = facturacionEnabled ? createAndPayVentaConFEFormSchema : createAndPayVentaFormSchema;
    const ventaSchema = facturacionEnabled ? ventaConFEFormSchema : ventaFormSchema;

    const form = useSchemaForm({
        schema: formSchema,
        onSubmit: async ({ value }) => {

            const isCredito = value.ventaRequest.credito === true;
            const isPresupuesto = value.ventaRequest.presupuesto === true;
            const feRequestedPago = !!value.pagoRequest?.pago?.facturacionElectronica;
            const feRequested = facturacionEnabled && feRequestedPago;

            // Presupuesto o sin pago
            if (value.pagoRequest == null || isPresupuesto) {
                const venta = await createMutation.mutateAsync(value.ventaRequest);
                openModal("printVenta", venta);
                notify("La venta se ha creado correctamente.");
                const vendedorId = ventaForm.getFieldValue("vendedorId");
                ventaForm.reset();
                ventaForm.setFieldValue("vendedorId", vendedorId);
                deleteCurrentDraft();
                return;
            }

            // Crédito
            if (isCredito) {
                if (facturacionEnabled) {
                    openModal("confirmFECredito", {
                        ventaRequest: value.ventaRequest,
                        onSuccess: () => {
                            const vendedorId = ventaForm.getFieldValue("vendedorId");
                            ventaForm.reset();
                            ventaForm.setFieldValue("vendedorId", vendedorId);
                            deleteCurrentDraft();
                        }
                    });
                    return;
                }

                // Crédito sin FE
                const venta = await createMutation.mutateAsync(value.ventaRequest);
                openModal("printVenta", venta);
                notify("La venta se ha creado correctamente.");
                const vendedorId = ventaForm.getFieldValue("vendedorId");
                ventaForm.reset();
                ventaForm.setFieldValue("vendedorId", vendedorId);
                deleteCurrentDraft();
                return;
            }

            // Contado
            if (feRequested) {
                checkEmisorConfig(config);

                const requestData = buildCreateAndPayFERequest({
                    ventaRequest: value.ventaRequest,
                    pagoRequest: value.pagoRequest,
                    monedaBase,
                    cotizacion,
                    transferenciaMetodos,
                    dEst: config?.establecimiento ?? "",
                    dPunExp: config?.puntoExpedicion ?? ""
                });

                const pago = await createAndPayFEMutation.mutateAsync(requestData);
                const fe = pago?.data?.facturaElectronica;

                if (fe?.kudeBase64) {
                    openPdfFromBase64(fe.kudeBase64);
                    notify(`La venta se ha creado y pagado correctamente. Se creo una factura electrónica N° ${fe.numeroDE}.`);
                } else {
                    notify("La venta se ha creado y pagado correctamente.");
                }
                return;
            }

            // Contado sin FE
            const pago = await createAndPayMutation.mutateAsync({
                ...value,
                ventaRequest: {
                    ...value.ventaRequest,
                    saltarFE: !feRequestedPago && mode === OperationalMode.GestionConFacturacion
                }
            });
            const venta = pago.venta;
            openModal("printVenta", venta);
            notify("La venta se ha creado y pagado correctamente.");
        }
    });

    const ventaForm = useSchemaForm({
        schema: ventaSchema,
        onSubmit: async ({ value }) => {
            form.setFieldValue("ventaRequest", value);
            if (!value.credito && !value.presupuesto) {
                const [cliente, vendedor] = await Promise.all([
                    getCliente(value.clienteId),
                    getVendedor(value.vendedorId)
                ])
                const venta = {
                    total: value.items.reduce((prev, curr) => prev + curr.precioUnitario * curr.cantidad, 0),
                    clienteId: cliente.id,
                    clienteNombre: cliente.nombre,
                    clienteNumDocumento: cliente.numDocumento,
                    vendedorId: vendedor.id,
                    vendedorNombre: vendedor.nombre,
                    credito: value.credito,
                    presupuesto: value.presupuesto,
                };
                openModal("pagarVenta", venta);
            } else {
                await form.handleSubmit();
            }
        },
        defaultValues: getVentaDefaultValues(facturacionEnabled, genericClientId)
    })

    const { otherDrafts, deleteCurrentDraft, deleteDraft } = useVentaDraft(ventaForm);

    const handleRestoreDraft = (draft: any) => {
        const v = draft.values;
        ventaForm.setFieldValue("vendedorId", v.vendedorId ?? "");
        ventaForm.setFieldValue("clienteId", v.clienteId ?? "");
        ventaForm.setFieldValue("fecha", v.fecha ?? localNow());
        ventaForm.setFieldValue("credito", v.credito ?? false);
        ventaForm.setFieldValue("presupuesto", v.presupuesto ?? false);
        ventaForm.setFieldValue("items", v.items ?? []);
        deleteDraft(draft.id);
    };

    if (!hasCajaAssigned) return <DataTable flex={1}>
        <DataTable.Content fit="flex" emptyText="Debes tener una caja asignada para poder crear una venta." />
    </DataTable>

    if (!isCajaOpen) return <DataTable flex={1}>
        <DataTable.Content fit="flex" emptyText="La caja debe estar abierta para poder crear una venta." />
    </DataTable>

    return <Form form={form} flex={1}>
        <VentaForm form={ventaForm} flex={1} onAfterReset={deleteCurrentDraft}
            draftRestoreSlot={<VentaDraftRestore drafts={otherDrafts} onRestore={handleRestoreDraft} onDelete={deleteDraft} />}
        />
        <Dialog code="pagarVenta" size="md" fullHeight>
            <ModalHeader>Pagar Venta</ModalHeader>
            <ModalValueExtractor>
                {venta => <SchemaFormHook
                    schema={cajaPagoFormSchema}
                    onSubmit={async ({ value }) => {
                        form.setFieldValue("pagoRequest", value);
                        await form.handleSubmit();
                        const vendedorId = ventaForm.getFieldValue("vendedorId");
                        ventaForm.reset();
                        ventaForm.setFieldValue("vendedorId", vendedorId);
                        deleteCurrentDraft();
                    }}
                    defaultValues={{
                        pago: {
                            items: [{ transferenciaMetodoId: transferenciaMetodoBase.id, monto: { items: [{ monedaId: monedaBase.id, cantidad: venta.total }] } }],
                            facturacionElectronica: true,
                        },
                        vuelto: { items: [{ transferenciaMetodoId: transferenciaMetodoBase.id, monto: { items: [{ monedaId: monedaBase.id, cantidad: 0 }] } }] },
                    }}
                    submitOnce
                    closeModalOnSubmit
                    preventCloseModalWhileSubmitting={false}
                >
                    {pagoForm => <CajaPagoForm venta={venta} form={pagoForm} padding={3} flex={1} />}
                </SchemaFormHook>}
            </ModalValueExtractor>
        </Dialog>
        <printer.Printer />
        <Dialog code="printVenta" size="xs">
            <ModalValueExtractor>
                {venta => <>
                    <ModalHeader>Imprimir Venta N°{venta.numeroVenta}</ModalHeader>
                    <VentaPrintForm venta={venta} padding={3} />
                </>}
            </ModalValueExtractor>
        </Dialog>
        <Dialog code="confirmFECredito" size="xs">
            <ModalValueExtractor>
                {(data) => <ConfirmFECreditoDialog
                    ventaRequest={data.ventaRequest}
                    createVentaConFECreditoMutation={createVentaConFECreditoMutation}
                    createMutation={createMutation}
                    config={config}
                    mode={mode}
                    onSuccess={data.onSuccess}
                />}
            </ModalValueExtractor>
        </Dialog>
        <EmisorConfigRequiredDialog />
    </Form>
}

// Dialog de confirmación de Facturación Electrónica a Crédito
function ConfirmFECreditoDialog({
    ventaRequest,
    createVentaConFECreditoMutation,
    createMutation,
    config,
    mode,
    onSuccess
}: any) {
    const [facturacionElectronica, setFacturacionElectronica] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { closeModal } = useModalHandlerContext();
    const openModal = useOpenModal();
    const { monedaBase } = useMonetizacion();
    const [monedaOperacion, setMonedaOperacion] = useState(ventaRequest.pago?.monedaOperacionId || monedaBase.id);
    const [conversionGuarani, setConversionGuarani] = useState(ventaRequest.pago?.conversionGuarani ?? 1);
    const checkEmisorConfig = useCheckEmisorConfig();

    const handleConfirm = async () => {
        setIsSubmitting(true);
        try {
            if (facturacionElectronica) {
                checkEmisorConfig(config);

                await handleCreateVentaConFECredito(
                    ventaRequest,
                    monedaBase,
                    createVentaConFECreditoMutation,
                    config?.establecimiento,
                    config?.puntoExpedicion,
                    monedaOperacion,
                    conversionGuarani
                );
            } else {
                const venta = await createMutation.mutateAsync({
                    ...ventaRequest,
                    saltarFE: mode === OperationalMode.GestionConFacturacion
                });
                openModal("printVenta", venta);
                notify("La venta se ha creado correctamente.");
            }

            onSuccess?.();
            closeModal("confirmFECredito");
        } catch (error: any) {
            if (error instanceof SilentSubmitError) return;
            notify(error?.response?.data || "Error al crear la venta", { variant: "error" });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        closeModal("confirmFECredito");
    };

    const monedaOperacionValue = {
        monedaOperacionId: monedaOperacion,
        conversionGuarani: conversionGuarani
    };

    const handleMonedaOperacionChange = (val: { monedaOperacionId: string; conversionGuarani: number }) => {
        setMonedaOperacion(val.monedaOperacionId);
        setConversionGuarani(val.conversionGuarani);
    };

    return (
        <Stack>
            <ModalHeader>Confirmar Tipo de Factura</ModalHeader>
            <Stack padding={2} gap={1}>
                <Typography>Si está marcada la casilla se generará una factura electrónica a crédito.</Typography>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={facturacionElectronica}
                            onChange={(e) => setFacturacionElectronica(e.target.checked)}
                        />
                    }
                    label="Facturación Electrónica"
                />
                {facturacionElectronica && (
                    <Stack direction="row" gap={2} paddingTop={1} alignItems="center">
                        <MonedaOperacionField
                            value={monedaOperacionValue}
                            onChange={handleMonedaOperacionChange}
                        />
                    </Stack>
                )}

                <Box display="flex" gap={2} justifyContent="flex-end">
                    <Button
                        variant="outlined"
                        onClick={handleCancel}
                        disabled={isSubmitting}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirm}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? "Procesando..." : "Confirmar"}
                    </Button>
                </Box>
            </Stack>
        </Stack>
    );
}